/**
 * Agent Skills (agentskills.io) open-standard conformance lint for SKILL.md
 * frontmatter — pure, advisory, never a hard gate.
 * See devlog: 2026-06-21-agentskills-conformance-shipped.
 * @docLink core/conformance
 */

/** A single agentskills.io conformance finding. */
export interface ConformanceFinding {
  /** `error` = hard standard violation; `warn` = portability risk. */
  level: "error" | "warn";
  /** Frontmatter field the finding concerns (e.g. "name", "metadata.tags"). */
  field: string;
  /** Human-readable, ready to print after a file path. */
  message: string;
}

/** Options for {@link checkAgentSkillConformance}. */
export interface ConformanceOptions {
  /**
   * Parent directory name, for the standard's `name === <dir>` rule.
   * Omit to skip that check (e.g. when validating a detached frontmatter blob).
   */
  dirName?: string;
}

// Standard limits — see https://agentskills.io/specification.
const NAME_MAX = 64;
const DESCRIPTION_MAX = 1024;
const COMPATIBILITY_MAX = 500;

/**
 * Check a SKILL.md frontmatter object against the Agent Skills open standard.
 * Returns findings only — the caller maps severity to exit codes.
 *
 * @param frontmatter Parsed SKILL.md YAML frontmatter.
 * @param opts Optional context (`dirName` enables the name==dir rule).
 * @returns Zero or more {@link ConformanceFinding}s.
 */
export function checkAgentSkillConformance(
  frontmatter: Record<string, unknown>,
  opts: ConformanceOptions = {},
): ConformanceFinding[] {
  const out: ConformanceFinding[] = [];
  const name = frontmatter.name;

  if (typeof name === "string") {
    if (name.length > NAME_MAX) {
      out.push({
        level: "error",
        field: "name",
        message: `name is ${name.length} chars (standard max ${NAME_MAX})`,
      });
    }
    if (opts.dirName !== undefined && name !== opts.dirName) {
      out.push({
        level: "warn",
        field: "name",
        message: `name "${name}" does not match parent directory "${opts.dirName}" (standard requires equality)`,
      });
    }
  }

  const description = frontmatter.description;
  if (typeof description === "string" && description.length > DESCRIPTION_MAX) {
    out.push({
      level: "error",
      field: "description",
      message: `description is ${description.length} chars (standard max ${DESCRIPTION_MAX})`,
    });
  }

  const compatibility = frontmatter.compatibility;
  if (typeof compatibility === "string" && compatibility.length > COMPATIBILITY_MAX) {
    out.push({
      level: "error",
      field: "compatibility",
      message: `compatibility is ${compatibility.length} chars (standard max ${COMPATIBILITY_MAX})`,
    });
  }

  // The standard defines metadata as a string→string map. skaile nests objects
  // here by design — report as a portability WARN, never an error, never rewrite.
  const metadata = frontmatter.metadata;
  if (metadata !== undefined && typeof metadata === "object" && metadata !== null) {
    for (const [k, v] of Object.entries(metadata as Record<string, unknown>)) {
      if (typeof v !== "string") {
        out.push({
          level: "warn",
          field: `metadata.${k}`,
          message: `metadata.${k} is ${Array.isArray(v) ? "an array" : typeof v}, not a string — non-portable to strict skills-ref consumers`,
        });
      }
    }
  }

  const allowedTools = frontmatter["allowed-tools"];
  if (allowedTools !== undefined && typeof allowedTools !== "string") {
    out.push({
      level: "warn",
      field: "allowed-tools",
      message: "allowed-tools must be a space-separated string",
    });
  }

  return out;
}
